import { SaveDesignToProfileModalEnum } from '@inter-ikea-kompis/component-save-design-to-profile';
import { KompisSaveDesignToProfile } from '@inter-ikea-kompis/react-components';
import { SkapaTheme } from '@inter-ikea-kompis/themes';
import { ReportSourceEnum } from 'analytics/reporters';
import { useScreenshot } from 'contexts/screenshot';
import { useServices } from 'contexts/services';
import { useServiceSettings } from 'contexts/serviceSettings';
import { useAppDispatch } from 'ducks/hooks';
import { LastSavedConfiguration } from 'ducks/layout';
import {
  hideSaveDesignToProfileSheet,
  isSaveDesignToProfileSheetOpen,
  showSaveDesignToProfileSheet
} from 'ducks/sheets';
import { getVpcCode } from 'ducks/vpc';
import { useSaveDesign } from 'hooks/useSaveDesign';
import { useVpc } from 'hooks/useVpc';
import { equals } from 'ramda';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

export const getMappedConfiguration = (
  configuration: any,
  prevSavedConfiguration: LastSavedConfiguration
): LastSavedConfiguration | undefined => {
  const mappedConfiguration = (configuration.entities as any)
    .map((e: any) => ({
      id: e.id,
      ref: e.ref,
      position: e.c.WorldTransformComponent
    }))
    .filter((e: any) => e.position);

  if ((configuration.entities as any)?.length - 1 !== prevSavedConfiguration?.length) {
    return mappedConfiguration;
  }

  // If the configuration is the same as the last one saved we don't need to save it again.
  // We show the toast saying we saved anyways because it will not matter to the user if we
  // actually saved or not.
  if (equals(prevSavedConfiguration, mappedConfiguration)) {
    return undefined;
  }

  return mappedConfiguration;
};

function SaveDesignToProfile() {
  const { translations } = useServiceSettings();
  const { accountService } = useServices();
  const dispatch = useAppDispatch();
  const vpc = useVpc();

  const vpcCode = useSelector(getVpcCode);
  const visible = useSelector(isSaveDesignToProfileSheetOpen);

  const [visibleModal, setVisibleModal] = useState<SaveDesignToProfileModalEnum | null>(null);
  const [loggedIn, setLoggedIn] = useState(false);
  const { vpcImages } = useScreenshot();

  const { saveToProfile, initiateLogin } = useSaveDesign({ setVisibleModal });

  useEffect(() => {
    if (visible) {
      setVisibleModal(SaveDesignToProfileModalEnum.saveDesignToProfileCard);
    } else {
      setVisibleModal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    if (visible) {
      accountService()
        .getUser()
        .then(async user => {
          setLoggedIn(!!user);

          if (!vpcCode || !vpcImages.length) {
            await vpc.save();
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const _initiateLogin = () => {
    initiateLogin(ReportSourceEnum.saveDesignSheet, { state: 'saveDesign' });
  };

  const onOpen = () => {
    dispatch(showSaveDesignToProfileSheet());
  };
  const onClose = () => {
    dispatch(hideSaveDesignToProfileSheet());
  };

  return (
    translations && (
      <KompisSaveDesignToProfile
        theme={SkapaTheme}
        translations={translations.kompis}
        visibleModal={visibleModal}
        loggedIn={loggedIn}
        onModalClose={onClose}
        onModalOpen={onOpen}
        onLogInAndSaveDesign={_initiateLogin}
        onSaveDesign={saveToProfile}
      />
    )
  );
}

export default SaveDesignToProfile;
